/**
 * Asset Type Detection Utilities
 *
 * This file contains utility functions for detecting and categorizing asset types
 * based on their properties, keys, and labels.
 */

import type { Asset } from '@/types';

/**
 * Checks if an asset is a web application based on its key
 *
 * @param asset - The asset to check
 * @returns True if the asset is a web application
 */
export const isWebApplication = (asset: Asset): boolean => {
  if (!asset.key) return false;
  return asset.key.startsWith('#webapplication#');
};

/**
 * Checks if an asset is an Active Directory (AD) object
 *
 * Performs two checks:
 * 1. Authoritative check: Looks for AD-specific prefixes in the asset key
 * 2. Secondary check: Verifies against known AD label types
 *
 * @param asset - The asset to check
 * @returns True if the asset is an AD object
 */
export const isADObject = (asset: Asset): boolean => {
  // Check if asset key starts with AD-specific prefixes (authoritative check)
  if (
    asset.key.match(
      /^#ad(user|computer|group|gpo|ou|container|domain|localgroup|localuser|aiaca|rootca|enterpriseca|ntauthstore|certtemplate|issuancepolicy)#/
    )
  ) {
    return true;
  }

  // Check for specific AD labels (secondary check for properly labeled AD objects)
  if (
    asset.label &&
    [
      'ADUser',
      'ADComputer',
      'ADGroup',
      'ADGPO',
      'ADOU',
      'ADContainer',
      'ADDomain',
      'ADLocalGroup',
      'ADLocalUser',
      'ADAIACA',
      'ADRootCA',
      'ADEnterpriseCA',
      'ADNTAuthStore',
      'ADCertTemplate',
      'ADIssuancePolicy',
    ].includes(asset.label)
  ) {
    return true;
  }

  return false;
};
